import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import AppWall from "./AppWall";
import { shouldShowAppWall, webPlatform } from "./appPromo";
import { sendEvent } from "./engagement";
import { ErrorBoundary } from "./ErrorBoundary";
import { initNativeApp, isNativeApp } from "./native";
import { isStandalone } from "./pwaInstall";
import { initObservability } from "./observability";
import "./styles.css";
import "./wow.css";
import "./elevate.css";
import "./pro.css";
import "./v2now.css";
import "./v3.css";
import "./atelier.css";
import "./edition.css";
import "./paper.css";
import "./retro.css";
// v4 layer loads last so it wins equal-specificity ties over every sheet above.
import "./v4.css";
// LUMEN: the white-gallery treatment of the light theme. Every rule is scoped
// to [data-theme="light"] so the other themes keep their souls.
import "./lumen.css";
// NOIR: the v4 identity. Cinema (dark) is the default voice of v4: the story's
// photograph paints the room, the Edition floats as a lit sheet over it. Loads
// last; rules are scoped to [data-theme="dark"] plus a few theme-neutral v4
// instruments (the filmstrip).
import "./noir.css";
// WEB GLASS: the site's material (lens edge, specular, depth, ambient tint) on top
// of the blur v4 already runs. Loads last so it wins equal-specificity ties.
import "./webglass.css";
// The simpler card (2026-09-13): one folio line, pointers, one disclosure. After every skin,
// before phoneread.css so the phone keeps its last word.
import "./simpler.css";
// The Kindle layer (2026-09-13): the reading face, the Aa sheet, the footer, Paper.
import "./kindle.css";
// PHONE WEB: the reader as it is laid out for a phone in a browser. Loads last of all, and
// every rule inside is scoped to html.phone-web, so it can only ever affect that one visitor.
import "./phoneread.css";
// CALM (2026-10-23): one house. The reader wears the same glass as /more: the shelf, the pills,
// the segmented lens, Fraunces for whatever arrives. Last of all, so it wins every tie; it changes
// what things are made of, never what the phone sheet shows or where.
import "./calm.css";
// The phone browser's swipe lesson, staged across the first three cards, and the interlude ask.
import "./lesson.css";
// THE THREE BARS (2026-10-30): the pillars and the sections under every shelf. Last of all,
// and doubled in specificity, because the home's lazy sheets load after everything here.
import "./masthead.css";
// QUIET (2026-11-02): the phone's bars, the Edition's top and the reading card, made calm. Last of all.
import "./quiet.css";
// THE DECK (2026-11-03): the topics with counts, a topic read to its end, the finished card. Last.
import "./deck.css";
import { initGlassPointer } from "./glassPointer";
import { isPhoneWeb } from "./phone";

initObservability();
void initNativeApp();
// The specular follows the cursor. Attaches nothing on touch devices or under
// reduced motion; see glassPointer.ts for the cost controls.
initGlassPointer();

// SELF-HEAL A BROKEN OR STALE CHUNK.
//
// Cloudflare Pages serves the SPA fallback for any path it cannot find, and `_headers` stamps
// everything under /assets/* with `immutable, max-age=31536000`. So a request that lands during
// the seconds between a new index.html going live and its assets propagating gets HTML back at a
// .js URL, WITH a one-year immutable header on it, and that wrong answer is then pinned in the
// edge cache under the asset's own name. It happened to insnip.com on 2026-08-04: the page white
// screened with "Expected a JavaScript-or-Wasm module script but the server responded with a MIME
// type of text/html", while the same deployment served perfectly on its *.pages.dev URL, because
// only one Accept-Encoding variant of the cache key had been poisoned.
//
// Vite fires `vite:preloadError` when a lazily-imported chunk fails to load, which is exactly this
// case. Reload once, with a cache-busting query so the document itself is re-fetched fresh; the
// sessionStorage latch means a genuinely offline reader gets the error boundary rather than a
// reload loop.
//
// IT HAPPENED AGAIN ON 2026-08-05, AND THIS HANDLER COULD NOT HELP, because that time the
// poisoned asset was the ENTRY module rather than a lazy chunk. This file is inside it, so none
// of the code below ever ran and the reader got a bare shell reading "Skip to content". The
// companion guard for that case lives in public/boot-guard.js, which is served with the ordinary
// rule rather than the immutable one and therefore still runs when this bundle will not parse.
// Keep the two in step: this one recovers a chunk, that one recovers the boot.
//
// AND AGAIN ON 2026-09-08, the third time. What the first two occurrences hid: reloading the
// DOCUMENT never fixed anything. index.html is served no-cache, so it was never the stale half.
// The reload came back and asked for the same hashed URL, whose poisoned copy is held under
// `immutable, max-age=31536000`, so the retry failed identically and the latch gave up. The
// asset has to be repaired before a reload is worth spending: `cache: "reload"` sends the
// request with no-cache, which makes the edge revalidate instead of answering from its poisoned
// copy, and re-stores the correct JavaScript under that key. Best-effort by construction, since
// either outcome still reloads and an offline reader behaves exactly as before.
window.addEventListener("vite:preloadError", (event) => {
  const KEY = "insnip.chunkReload";
  try {
    if (sessionStorage.getItem(KEY)) return; // already tried once this session: let it surface
    sessionStorage.setItem(KEY, "1");
  } catch {
    return; // no sessionStorage (private mode): never risk a loop
  }
  event.preventDefault();
  const reload = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("_r", String(Date.now()).slice(-6));
    window.location.replace(url.toString());
  };
  // Vite names the chunk it could not preload on the event payload; the message carries the URL
  // when the property does not. Repair that exact key, then reload.
  const payload = (event as Event & { payload?: { message?: string } }).payload;
  const found = /https?:\/\/\S+?\.js/.exec(payload?.message ?? "")?.[0];
  if (found && typeof fetch === "function") {
    fetch(found, { cache: "reload", credentials: "same-origin" }).then(reload, reload);
  } else {
    reload();
  }
});

// Stamp the saved theme BEFORE first paint. App.tsx applies it in an effect, which is
// after paint: without this, the bare :root fallback palette (indigo/purple) flashes
// and can be captured as "the" chrome color. Same key + JSON shape as App.tsx.
//
// v4 NOIR migration: Cinema (dark) is v4's default voice. Returning visitors carry an
// insnip.theme written by an older build (often set while exploring the theme menu),
// which would hide the redesign behind their stale choice, so the FIRST load of this
// build lands everyone on Cinema once (guarded by insnip.themeV4). The theme picker
// still works and every later choice sticks.
//
// Midnight (oled) and Auto were retired from the picker (see THEME_OPTIONS in App.tsx): Auto
// only ever resolved to Cinema or Daylight, and Midnight was a shade of Cinema. Anyone holding
// one is moved to Cinema HERE, pre-paint, and the stored value is rewritten, so they are never
// left wearing a theme the menu can no longer give back to them.
try {
  const KNOWN = ["dark", "light", "sepia", "retro"];
  const RETIRED = ["oled", "auto"];
  let theme = "dark";
  if (localStorage.getItem("insnip.themeV4") === "1") {
    const stored = localStorage.getItem("insnip.theme");
    const parsed = stored ? JSON.parse(stored) : "dark";
    if (RETIRED.includes(parsed)) {
      theme = "dark";
      localStorage.setItem("insnip.theme", JSON.stringify("dark"));
    } else {
      theme = KNOWN.includes(parsed) ? parsed : "dark";
    }
  } else {
    localStorage.setItem("insnip.themeV4", "1");
    localStorage.setItem("insnip.theme", JSON.stringify("dark"));
  }
  document.documentElement.setAttribute("data-theme", theme);
} catch {
  document.documentElement.setAttribute("data-theme", "dark");
}

// THE FRONT DOOR REMEMBERS YOU. "/" is served by functions/index.js: a stranger gets the pitch
// built from today's cards, and anyone carrying this cookie gets the app's home instead, with
// no forward and no flash. The cookie is set here, on every boot of the app, so the first time
// someone reads here is the last time they see the door (unless they ask for it with ?stay=1).
try {
  document.cookie = "insnip_home=1; Max-Age=31536000; Path=/; SameSite=Lax; Secure";
  // "?enter=1" has done its work (the door set the cookie and served this shell): drop it from
  // the address so a reload or a share of the home carries a clean "/".
  if (window.location.pathname === "/" && /(?:^|[?&])enter=1(?:&|$)/.test(window.location.search)) {
    window.history.replaceState(null, "", `/${window.location.hash}`);
  }
} catch {
  /* cookies blocked: the door's own script forwards from localStorage instead */
}

// THE PHONE WALL, DECIDED BEFORE THE READER MOUNTS.
//
// Not an early return inside App(): that component runs some three thousand lines of hooks
// before its first return, so a conditional return there would break the rules of hooks, and
// even placed after them it would still have fetched the feed, restored the session and started
// the round for a reader who is never shown any of it. Deciding at the mount point means a
// walled visitor costs one user-agent test and zero requests.
//
// Everything about WHO is walled lives in appPromo.shouldShowAppWall, next to the rules it
// shares with the server gate in functions/_device.js.
const wallPlatform = webPlatform();
const showWall = shouldShowAppWall({ nativeApp: isNativeApp, standalone: isStandalone() });

// PHONE WEB, STAMPED BEFORE FIRST PAINT.
//
// html, body and #root are ANCESTORS of the app shell, so a descendant selector inside the
// React tree can never reach them, and a class applied in an effect lands after the first
// frame. Both problems are solved by stamping the root element here, next to the theme, which
// is the same technique the native shell uses for its own ground colour.
//
// The class is the ONLY switch for phoneread.css, so every rule in that sheet is inert on a
// desktop, a tablet, the native app, an installed PWA, a crawler, and ?desktop=1.
// Never on /more: phoneread.css pins html and body to one screen with overflow hidden (the
// reader scrolls inside its card), and on the tools pages that froze the whole page on a phone.
try {
  if (isPhoneWeb() && !/^\/more(\/|$)/.test(window.location.pathname) && window.location.pathname !== "/") document.documentElement.classList.add("phone-web");
} catch {
  /* a class name can never be worth a boot failure */
}

// CHECK OUT MORE (/more and /more/<tool>), DECIDED AT THE SAME MOUNT POINT AS THE WALL.
//
// The tools are their own app: no reader, no round, no session restore. A lazy chunk keeps the
// reader's entry bundle exactly as it was, and the wall is skipped on purpose, because a tool
// somebody reached from a search or a shared link is the door, not the room behind it.
const MoreApp = lazy(() => import("./more/MoreApp"));
// THE HOME (2026-10-24) is "/": the lead story, the reader's day, today's ten and every tool, in
// the same chunk as the tools. A deep link that used to land on the old landing page and be
// forwarded to the reader (a push's #card=, a shared ?ref=, the quiz) still goes to the reader;
// the rule mirrors functions/_landing.js shouldEnterReader, minus the returning-visitor case,
// because the home is now the page a returning visitor wants.
const atRoot = window.location.pathname === "/";
if (atRoot && (/card=|quiz|game/.test(window.location.hash) || /(?:^|[?&])(?:ref|r)=/.test(window.location.search))) {
  window.location.replace(`/read${window.location.search}${window.location.hash}`);
}
const onMore = atRoot || /^\/more(\/|$)/.test(window.location.pathname);
// The reader lives at /read (and the shell file itself). Any other path that reached this bundle
// is an address nothing lives at: a mistyped link, or a shell the service worker handed back
// offline. It gets the same not-found page the server draws (public/404.html), never the reader
// pretending the address was fine.
const knownPath = onMore || /^\/(?:read(?:\/|$)|index\.html$)/.test(window.location.pathname);

function NotFound() {
  const path = window.location.pathname;
  return (
    <div className="static-page" style={{ minHeight: "100vh", background: "var(--bg, #0b0a07)", color: "var(--ink, #f3ecda)", fontFamily: "Inter, system-ui, sans-serif" }}>
      <link rel="stylesheet" href="/site-chrome.css" />
      <nav className="site-nav" aria-label="Site">
        <div className="site-shelf">
          <a className="site-brand" href="/" aria-label="InSnip"><span className="b-in">In</span><span className="b-snip">Snip</span></a>
          <span className="site-pillars">
            <a href="/">Home</a>
            <a href="/read">News</a>
            <a href="/read#edition">Edition</a>
            <a className="site-app" href="/get"><span>App</span></a>
          </span>
        </div>
      </nav>
      <main style={{ maxWidth: 560, margin: "56px auto 0", padding: "0 20px 64px" }}>
        <h1 style={{ fontFamily: "Georgia, serif", fontWeight: 400, fontSize: 34, lineHeight: 1.12, margin: "0 0 10px" }}>We could not find that address</h1>
        <p style={{ color: "var(--muted, #b3aa92)", fontSize: 15, margin: "0 0 6px" }}>Nothing lives at <code style={{ fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 14, color: "var(--ink, #f3ecda)" }}>{path}</code>.</p>
        <p style={{ color: "var(--muted, #b3aa92)", fontSize: 16, margin: "0 0 22px" }}>It may have moved on, or the link lost a character on the way. The day's news has not.</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, margin: "0 0 28px" }}>
          <a href="/read#edition" style={{ display: "inline-flex", alignItems: "center", minHeight: 44, padding: "0 16px", borderRadius: 999, background: "var(--gold, #eec25b)", color: "#171004", textDecoration: "none", fontWeight: 600, fontSize: 15 }}>Read today's edition</a>
          <a href="/" style={{ display: "inline-flex", alignItems: "center", minHeight: 44, padding: "0 16px", borderRadius: 999, border: "1px solid rgba(247,219,142,.3)", color: "inherit", textDecoration: "none", fontWeight: 600, fontSize: 15 }}>Home</a>
          <a href="/more" style={{ display: "inline-flex", alignItems: "center", minHeight: 44, padding: "0 16px", borderRadius: 999, border: "1px solid rgba(247,219,142,.3)", color: "inherit", textDecoration: "none", fontWeight: 600, fontSize: 15 }}>The tools</a>
        </div>
        <form action="/read" method="get" style={{ display: "flex", gap: 8 }}>
          <input type="search" name="q" placeholder="Search today's stories, places and tools" aria-label="Search" style={{ flex: 1, minWidth: 0, minHeight: 44, padding: "0 14px", borderRadius: 12, border: "1px solid rgba(247,219,142,.2)", background: "var(--panel, #15130d)", color: "inherit", font: "inherit", fontSize: 16 }} />
          <button type="submit" style={{ minHeight: 44, padding: "0 16px", borderRadius: 12, border: 0, background: "var(--panel, #15130d)", color: "var(--gold, #eec25b)", font: "inherit", fontWeight: 700, cursor: "pointer" }}>Search</button>
        </form>
      </main>
    </div>
  );
}

createRoot(document.getElementById("root") as HTMLElement).render(
  <StrictMode>
    <ErrorBoundary>
      {!knownPath ? (
        <NotFound />
      ) : onMore ? (
        <Suspense fallback={<div className="more-boot" aria-busy="true" />}>
          <MoreApp />
        </Suspense>
      ) : showWall ? (
        <AppWall
          platform={wallPlatform}
          onSeen={() => sendEvent({ type: "app_wall_seen", tag: wallPlatform })}
          onStoreClick={(surface) => sendEvent({ type: "app_get_click", tag: surface })}
        />
      ) : (
        <App />
      )}
    </ErrorBoundary>
  </StrictMode>
);

// The boot succeeded, so release the entry-module guard's one-shot latch (see index.html).
// Clearing it HERE rather than only in that guard's own timer means the next genuinely broken
// boot gets its retry too: a reader whose session latched during an outage would otherwise
// carry the latch for the rest of the session and never self-heal a second time.
try {
  sessionStorage.removeItem("insnip.bootReload");
} catch {
  /* no sessionStorage (private mode): the guard never latched, so there is nothing to clear */
}

// Register the service worker for offline support + installability (best-effort).
if ("serviceWorker" in navigator) {
  // When a NEW service worker version is deployed it skipWaiting()s and claims clients,
  // firing `controllerchange`. Reload once so the user immediately gets the fresh app
  // shell instead of being stuck on a stale cached bundle (e.g. an old build whose API
  // URL wasn't wired up, which silently falls back to the bundled cards.json). Guarded so
  // it only fires on a real UPDATE (page already had a controller), never the first
  // install, and only once (no reload loop).
  if (navigator.serviceWorker.controller) {
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  }
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
  // A tapped web push (public/firebase-messaging-sw.js) hands this tab the story's URL by
  // message rather than navigating it: that worker is scoped to /firebase-push/ and controls
  // no page. Only a URL on this origin is followed, checked here as well as in the worker.
  navigator.serviceWorker.addEventListener("message", (event) => {
    try {
      const data = (event as MessageEvent).data as { type?: unknown; url?: unknown } | null;
      if (!data || data.type !== "insnip:notificationclick" || typeof data.url !== "string") return;
      const target = new URL(data.url, window.location.origin);
      if (target.origin === window.location.origin) window.location.href = target.href;
    } catch {
      /* a malformed message is ignored */
    }
  });
}
